import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Send, Paperclip, KeyRound, ShieldCheck, MapPin, Calendar, IndianRupee, Play, Upload, CheckCircle2, RefreshCcw, Copy, X, Info } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { Timeline } from "../../components/shared/Timeline";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { FileUpload } from "../../components/shared/FileUpload";
import { studentNav, companyNav, adminNav } from "../../components/shared/navConfig";
import { useAuth } from "../../context/AppContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { toast } from "sonner";

const STATIC_STEPS = [
  { key: "allocated",      label: "Allocated" },
  { key: "checked_in",     label: "Checked In" },
  { key: "work_started",   label: "Work Started" },
  { key: "submitted",      label: "Submitted" },
  { key: "approved",       label: "Approved" },
  { key: "completed",      label: "Completed" },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function Workroom() {
  const { role, user } = useAuth();
  const [params] = useSearchParams();
  const wantedId = params.get("id");
  const nav = role === "company" ? companyNav : role === "admin" ? adminNav : studentNav;
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([]);
  const [wk, setWk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [companyOtp, setCompanyOtp] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitNote, setSubmitNote] = useState("");
  const [submitFiles, setSubmitFiles] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(null); // "approve" | "request_revision"
  const [reviewNote, setReviewNote] = useState("");
  const listRef = useRef(null);

  const loadList = async () => {
    try {
      const { data } = await api.get("/workrooms/mine");
      setRooms(data);
      return data;
    } catch { return []; }
  };

  const loadRoom = async (id) => {
    try { const { data } = await api.get(`/workrooms/${id}`); setWk(data); return data; }
    catch { return null; }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const list = await loadList();
      const id = wantedId || list[0]?.workroom_id;
      if (id) await loadRoom(id);
      setLoading(false);
    })();
  }, [wantedId]); // eslint-disable-line

  // Poll for messages/updates
  useEffect(() => {
    if (!wk?.workroom_id) return;
    const t = setInterval(() => loadRoom(wk.workroom_id), 5000);
    return () => clearInterval(t);
  }, [wk?.workroom_id]);

  useEffect(() => { listRef.current?.scrollTo(0, listRef.current.scrollHeight); }, [wk?.messages?.length]);

  const timelineSteps = useMemo(() => {
    if (!wk) return STATIC_STEPS.map((s, i) => ({ ...s, id: i, status: "future", ts: "—" }));
    const events = wk.timeline || [];
    const eventMap = Object.fromEntries(events.map(e => [e.key, e]));
    const doneKeys = new Set(events.map(e => e.key));
    const active = events[events.length - 1]?.key;
    return STATIC_STEPS.map((s, i) => {
      const done = doneKeys.has(s.key);
      const isActive = s.key === active && wk.status !== "Completed";
      return {
        id: i, key: s.key, label: s.label,
        ts: eventMap[s.key] ? fmt(eventMap[s.key].ts) : "—",
        status: wk.status === "Completed" && s.key === "completed" ? "done" : done ? (isActive ? "active" : "done") : "future",
      };
    });
  }, [wk]);

  const send = async () => {
    if (!input.trim() || !wk) return;
    try {
      await api.post(`/workrooms/${wk.workroom_id}/messages`, { text: input });
      setInput("");
      loadRoom(wk.workroom_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const generateOtp = async () => {
    try {
      const { data } = await api.post(`/workrooms/${wk.workroom_id}/generate-otp`);
      setCompanyOtp(data.otp);
      toast.success("OTP generated — share with your student");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return;
    try {
      await api.post(`/workrooms/${wk.workroom_id}/check-in`, { otp });
      setOtpOpen(false); setOtp("");
      toast.success("Checked in successfully");
      loadRoom(wk.workroom_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startWork = async () => {
    try { await api.post(`/workrooms/${wk.workroom_id}/start`); toast.success("Work started"); loadRoom(wk.workroom_id); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitWork = async () => {
    try {
      await api.post(`/workrooms/${wk.workroom_id}/submit`, {
        notes: submitNote,
        deliverable_file_ids: submitFiles.map(f => f.file_id),
      });
      setSubmitOpen(false); setSubmitNote(""); setSubmitFiles([]);
      toast.success("Submitted for company review");
      loadRoom(wk.workroom_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reviewWork = async () => {
    try {
      await api.post(`/workrooms/${wk.workroom_id}/review`, { decision: reviewOpen, note: reviewNote || null });
      setReviewOpen(null); setReviewNote("");
      toast.success(reviewOpen === "approve" ? "Approved & completed" : "Revisions requested");
      loadRoom(wk.workroom_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return <DashboardShell nav={nav} title="Workroom"><div className="text-sm text-zinc-500">Loading…</div></DashboardShell>;
  }
  if (!wk) {
    return (
      <DashboardShell nav={nav} title="Workroom" subtitle="No active workrooms yet">
        <div className="card-surface p-12 text-center text-sm text-zinc-500">
          <ShieldCheck size={22} className="mx-auto mb-3 text-zinc-600"/>
          Once you are allocated to a task, its workroom will appear here.
        </div>
      </DashboardShell>
    );
  }

  const task = wk.task || {};
  const isStudent = wk.student_user_id === user?.user_id;
  const isCompany = wk.company_user_id === user?.user_id;
  const canCheckIn = isStudent && ["Allocated", "Revision Required"].includes(wk.status);
  const canStart = isStudent && wk.status === "Checked In";
  const canSubmit = isStudent && (wk.status === "In Progress" || wk.status === "Revision Required");
  const canReview = isCompany && wk.status === "Submitted";
  const canCompanyOtp = isCompany && ["Allocated", "Revision Required"].includes(wk.status);

  return (
    <DashboardShell nav={nav} title="Workroom" subtitle={`${task.task_id || wk.task_id} · ${task.title || ""}`}>
      {rooms.length > 1 && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <span className="text-xs text-zinc-500">Workrooms:</span>
          {rooms.map(r => (
            <button key={r.workroom_id} onClick={() => navigate(`/workroom?id=${r.workroom_id}`)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${r.workroom_id === wk.workroom_id ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-[#27272A] text-zinc-400 hover:border-[#3A3A42]"}`}>
              {r.task?.title?.slice(0, 24) || r.task_id}
            </button>
          ))}
        </div>
      )}

      {/* Task header */}
      <div className="card-surface p-5 mb-6 grid md:grid-cols-4 gap-5">
        <div className="md:col-span-2">
          <div className="eyebrow">Task</div>
          <div className="font-display text-lg text-white mt-1">{task.title || "—"}</div>
          <div className="text-xs text-zinc-500 mt-1">{task.company_name} · <span className="font-mono">{wk.task_id}</span></div>
        </div>
        <TaskMeta l="Location" v={task.city || task.address || task.work_mode || "—"} icon={MapPin}/>
        <TaskMeta l="Deadline" v={task.deadline || "—"}        icon={Calendar}/>
        <div className="md:col-span-2 flex items-center gap-4 flex-wrap text-xs">
          <span className="text-zinc-500">Company</span>
          <span className="text-zinc-200">{wk.company?.name || "—"}</span>
          <span className="text-zinc-500">Student</span>
          <span className="text-zinc-200">{wk.student?.name || "—"}</span>
        </div>
        <TaskMeta l="Budget" v={`₹${Number(task.budget || 0).toLocaleString()}`} icon={IndianRupee}/>
        <div className="flex items-center justify-end"><StatusBadge status={wk.status}/></div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-6">
          <div className="card-surface p-6">
            <div className="eyebrow mb-4">Progress timeline</div>
            <Timeline steps={timelineSteps}/>
          </div>

          {/* Company: generate OTP */}
          {canCompanyOtp && (
            <div className="card-surface p-6 relative overflow-hidden">
              <div className="absolute inset-0 grid-bg-fine opacity-60"/>
              <div className="relative">
                <div className="eyebrow text-cyan-300">Check-in window</div>
                <h3 className="font-display text-lg text-white mt-1">Open check-in for your student</h3>
                {companyOtp ? (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Share this OTP</div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="font-mono text-3xl text-blue-300 tracking-widest">{companyOtp}</div>
                      <button data-testid="copy-otp-btn" onClick={() => { navigator.clipboard.writeText(companyOtp); toast.success("Copied"); }} className="btn-ghost px-2 py-1.5 rounded-md text-xs">
                        <Copy size={12}/>
                      </button>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-2">Valid for 2 hours. The OTP is single-use.</div>
                  </div>
                ) : (
                  <button data-testid="company-gen-otp-btn" onClick={generateOtp} className="btn-primary w-full py-2.5 rounded-md mt-4 text-sm inline-flex items-center justify-center gap-2 uppercase tracking-widest">
                    <KeyRound size={13}/> Generate OTP
                  </button>
                )}
                <button data-testid="company-regen-otp-btn" onClick={generateOtp} className="btn-ghost w-full py-2 rounded-md mt-2 text-xs inline-flex items-center justify-center gap-2">
                  <RefreshCcw size={11}/> Regenerate
                </button>
              </div>
            </div>
          )}

          {/* Student: check-in / start / submit */}
          {canCheckIn && (
            <div className="card-surface p-6">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-cyan-400 pulse-dot"/><div className="eyebrow text-cyan-300">Ready to check in</div></div>
              <h3 className="font-display text-lg text-white mt-2">Enter check-in OTP</h3>
              <p className="text-xs text-zinc-500 mt-1.5">Ask the company for the 6-digit code to log your attendance.</p>
              <button data-testid="open-otp-modal-btn" onClick={() => setOtpOpen(true)} className="btn-primary w-full py-2.5 rounded-md mt-5 text-sm inline-flex items-center justify-center gap-2 uppercase tracking-widest">
                <KeyRound size={13}/> Check in with OTP
              </button>
            </div>
          )}

          {canStart && (
            <div className="card-surface p-6">
              <div className="eyebrow">Ready to start</div>
              <h3 className="font-display text-lg text-white mt-1">Begin work</h3>
              <p className="text-xs text-zinc-500 mt-1.5">You are checked in. Start the clock to unlock deliverable submission.</p>
              <button data-testid="start-work-btn" onClick={startWork} className="btn-primary w-full py-2.5 rounded-md mt-4 text-sm inline-flex items-center justify-center gap-2">
                <Play size={13}/> Start work
              </button>
            </div>
          )}

          {canSubmit && (
            <div className="card-surface p-6">
              <div className="eyebrow">Ready when you are</div>
              <h3 className="font-display text-lg text-white mt-1">Submit deliverables</h3>
              <p className="text-xs text-zinc-500 mt-1.5">Upload your work and add notes for the company to review.</p>
              <button data-testid="open-submit-btn" onClick={() => setSubmitOpen(true)} className="btn-primary w-full py-2.5 rounded-md mt-4 text-sm inline-flex items-center justify-center gap-2">
                <Upload size={13}/> Submit work
              </button>
            </div>
          )}

          {canReview && (
            <div className="card-surface p-6">
              <div className="eyebrow">Awaiting your review</div>
              <h3 className="font-display text-lg text-white mt-1">Approve or request revisions</h3>
              {wk.submission?.notes && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">Student notes: "{wk.submission.notes}"</p>}
              {wk.submission?.file_ids?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {wk.submission.file_ids.map(fid => (
                    <a key={fid} target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download`}
                       className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Paperclip size={11}/> Deliverable</a>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button data-testid="approve-work-btn" onClick={() => setReviewOpen("approve")} className="btn-primary py-2 rounded-md text-xs inline-flex items-center justify-center gap-1.5">
                  <CheckCircle2 size={12}/> Approve
                </button>
                <button data-testid="revise-work-btn" onClick={() => setReviewOpen("request_revision")} className="btn-ghost py-2 rounded-md text-xs inline-flex items-center justify-center gap-1.5">
                  <RefreshCcw size={12}/> Revisions
                </button>
              </div>
            </div>
          )}

          {wk.status === "Completed" && (
            <div className="card-surface p-6">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.35)" }}>
                <CheckCircle2 className="text-emerald-400" size={16}/>
              </div>
              <div className="font-display text-lg text-white">Task complete</div>
              <p className="text-xs text-zinc-500 mt-1">Great work. Payment will follow shortly.</p>
            </div>
          )}
        </div>

        {/* Chat */}
        <div className="lg:col-span-8 card-surface flex flex-col h-[640px]">
          <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
            <div>
              <div className="eyebrow">Workroom chat</div>
              <div className="text-sm text-white mt-0.5">{wk.company?.name} ↔ {wk.student?.name}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 text-xs text-zinc-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/> Live</div>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {(wk.messages || []).length === 0 && (
              <div className="text-center text-xs text-zinc-500 mt-8">
                <Info size={16} className="mx-auto mb-2 text-zinc-600"/>
                No messages yet. Say hello!
              </div>
            )}
            {(wk.messages || []).map(m => {
              const mine = m.from_user_id === user?.user_id;
              return (
                <div key={m.created_at + m.from_user_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-md px-4 py-2.5 rounded-lg border ${mine ? "bg-blue-500/10 border-blue-500/25" : "bg-[#111114] border-[#1E1E22]"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{m.from_name}</div>
                    <div className="text-sm text-zinc-100 leading-relaxed">{m.text}</div>
                    <div className="text-[10px] text-zinc-500 mt-1 font-mono">{fmt(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-5 py-4 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <input
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Write a message…"
              className="flex-1 px-3 py-2 rounded-md border bg-[#0A0A0C] text-sm text-white placeholder:text-zinc-600 outline-none"
              style={{ borderColor: "var(--border-strong)" }}
            />
            <button data-testid="chat-send-btn" onClick={send} className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2">
              <Send size={13}/> Send
            </button>
          </div>
        </div>
      </div>

      {/* OTP Dialog */}
      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent className="max-w-sm" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Check-in with OTP</DialogTitle>
            <DialogDescription className="text-zinc-500">Enter the 6-digit code shared by the company.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
              <InputOTPGroup>{[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="border-[#27272A] bg-[#111114] text-white"/>)}</InputOTPGroup>
            </InputOTP>
          </div>
          <DialogFooter>
            <button onClick={() => setOtpOpen(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button data-testid="verify-otp-btn" onClick={verifyOtp} className="btn-primary px-4 py-2 rounded-md text-sm">Verify & check in</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-lg" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Submit deliverables</DialogTitle>
            <DialogDescription className="text-zinc-500">Attach your work and add a short note.</DialogDescription>
          </DialogHeader>
          <textarea data-testid="submit-notes" value={submitNote} onChange={e => setSubmitNote(e.target.value)} rows={4}
                    placeholder="Summarize what you delivered…"
                    className="w-full px-3 py-2 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none resize-none focus:border-blue-500/50"
                    style={{ borderColor: "var(--border-strong)" }}/>
          <FileUpload
            value={null}
            onChange={v => v && setSubmitFiles(x => [...x, v])}
            purpose="deliverable"
            testId="submit-file-upload"
            label="Add deliverable file"
            hint="Choose a file to upload"
          />
          {submitFiles.length > 0 && (
            <div className="space-y-1.5">
              {submitFiles.map((f, i) => (
                <div key={f.file_id} className="flex items-center gap-2 px-3 py-2 rounded-md border text-xs" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                  <Paperclip size={11} className="text-blue-400"/>
                  <span className="flex-1 truncate text-white">{f.filename}</span>
                  <button onClick={() => setSubmitFiles(x => x.filter((_, k) => k !== i))} className="text-zinc-500 hover:text-rose-400"><X size={12}/></button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setSubmitOpen(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button data-testid="submit-confirm-btn" onClick={submitWork} className="btn-primary px-4 py-2 rounded-md text-sm">Submit</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewOpen} onOpenChange={() => setReviewOpen(null)}>
        <DialogContent className="max-w-md" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{reviewOpen === "approve" ? "Approve deliverables" : "Request revisions"}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {reviewOpen === "approve" ? "Mark this task complete and release payment." : "Send feedback to the student."}
            </DialogDescription>
          </DialogHeader>
          <textarea data-testid="review-note-input" value={reviewNote} onChange={e => setReviewNote(e.target.value)} rows={3}
                    placeholder="Optional note to the student…"
                    className="w-full px-3 py-2 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none resize-none focus:border-blue-500/50"
                    style={{ borderColor: "var(--border-strong)" }}/>
          <DialogFooter>
            <button onClick={() => setReviewOpen(null)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button data-testid="review-confirm-btn" onClick={reviewWork} className="btn-primary px-4 py-2 rounded-md text-sm">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

const TaskMeta = ({ l, v, icon: Icon }) => (
  <div>
    <div className="eyebrow flex items-center gap-1.5"><Icon size={11}/>{l}</div>
    <div className="text-sm text-zinc-200 mt-1">{v}</div>
  </div>
);
